import type { ChatOpenPayload } from '../aiChatTypes';

export type OverviewCardAiSnapshot = Record<string, unknown>;

export type OverviewCardAiPrompt = {
  /** Short text shown in the chat bubble (no "card"). */
  userMessage: string;
  /** Icon button tooltip; should include the word "AI". */
  tooltip: string;
  /**
   * Model-only instructions. Ask for insight quality similar to a strong
   * financial coach summary: context, drivers, comparisons, and a follow-up.
   */
  modelInstructions: string;
};

const SHARED_OUTPUT_GUIDANCE = `
Write for a busy person scanning the Overview dashboard. Be specific with dollar amounts and dates when available.
Structure the reply like this:
1) One or two sentences that answer "what happened / where do I stand?" Call out anything that looks alarming but is actually benign (e.g. newly connected accounts, one-time transfers, timing quirks).
2) 3–5 short bullets with the biggest drivers, each including amounts and relative impact when you can (share of change or share of total).
3) Brief comparison to the prior period or typical pattern when tools/data allow.
4) End with ONE concrete follow-up question the user can answer with "yes" to dig deeper.

Use tools to pull fresh data; treat any on-screen snapshot only as a hint for what the user is looking at. Prefer plain language over jargon. Do not invent accounts or amounts you cannot verify.
`.trim();

const prompt = (
  userMessage: string,
  tooltip: string,
  body: string,
  outputGuidance: string = SHARED_OUTPUT_GUIDANCE,
): OverviewCardAiPrompt => ({
  userMessage,
  tooltip,
  modelInstructions: `${body.trim()}\n\n${outputGuidance.trim()}`,
});

const PROMPTS_BY_CARD_KEY: Record<string, OverviewCardAiPrompt> = {
  netWorth: prompt(
    "What's driving my net worth right now?",
    'Ask AI what’s driving your net worth',
    `The user tapped Summarize on their Overview Net Worth view. They want a clear story of their financial position, not a restatement of the headline number.

Explain how net worth changed over the selected period and WHY. Distinguish real gains/losses from bookkeeping events (connecting/disconnecting accounts, transfers between owned accounts). Call get_net_worth for the current total and the largest asset and liability drivers. Call generate_report with reportType net-worth for the change over that period, and set dateRange from the snapshot period. Treat the on-screen snapshot only as a hint for the period and headline the user is looking at. Suggest a follow-up about debt payoff impact, asset growth, or the next best lever.`,
  ),
  spendingPlan: prompt(
    'Am I on track with my Spending Plan this month?',
    'Ask AI if your Spending Plan is on track',
    `The user tapped Summarize on their Overview Spending Plan. They want to know if they are on track this month and what to watch.

Cover left-to-spend / available, how much is already spent vs planned, and whether the pace looks sustainable for the rest of the month. Flag the planned categories or bills most at risk of overspending. Call out anything misleading (e.g. large upcoming bill that hasn't posted yet, or a one-time expense). Suggest a follow-up about cutting a category, adjusting a plan, or closing out last month if relevant.`,
  ),
  upcoming: prompt(
    "What's coming due, and will I be okay?",
    'Ask AI what’s coming due next',
    `The user tapped Summarize on their Overview Bills & Income. They want to know what money is coming in and going out next, and whether timing creates a squeeze.

Summarize the next due bills/income (payee, amount, date). Highlight the largest upcoming outflow and any cluster of due dates that could strain cash. Note income expected before those bills if available. Flag past-due or unusually large items. Suggest a follow-up about covering a shortfall, which bill to pay first, or skipping/pausing a subscription.`,
  ),
  transactions: prompt(
    'Review the last 4 days of transactions and let me know if anything needs to be fixed or changed.',
    'Ask AI to review the last 4 days',
    `The user tapped Summarize on their Overview Recent Transactions. They want a read on the last 4 days, and to be told if anything needs to be fixed or changed.

Stay within the last 4 days only; ignore older transactions. Many people will have nothing to rename or recategorize. Do not invent those tasks. Mention a payee rename, a category change, or a suspicious charge only when you actually see it (unexpected merchant, unusual amount, possible duplicate, or unfamiliar charge). If something else needs attention, include that too.`,
    `Write for a busy person scanning the Overview dashboard. Be specific with dollar amounts and dates when available.
Structure the reply like this:
1) Open with a short summary paragraph that only states the totals for the last 4 days: how many transactions, total spent, and total money in if any. Use real amounts. Do not lead with action items or a story.
2) Then 3–5 bullets. Each bullet is one line only: payee, amount, and date, plus a few words on why it matters. Do not write a paragraph under a bullet.
3) Brief comparison to the prior few days or a typical pattern when tools/data allow.
4) End with ONE concrete follow-up question the user can answer with "yes". If the same payee or category issue shows up as a pattern, the follow-up can be about fixing that payee name or category. If nothing needs a fix, ask about a notable transaction instead. Do not suggest a rename or category change when there is nothing to fix.

Use tools to pull fresh data; treat any on-screen snapshot only as a hint for what the user is looking at. Prefer plain language over jargon. Do not invent accounts or amounts you cannot verify.`,
  ),
  spendingCats: prompt(
    'Where is most of my money going this month?',
    'Ask AI where most of your money is going',
    `The user tapped Summarize on their Overview Top Spending Categories (this month). They want to know where money is going and whether the mix looks healthy.

List the top categories with amounts and share of total spending. Call out the biggest category and anything that looks high for this point in the month. Compare to a typical month or prior month when tools allow. Note if a few categories dominate. Suggest a follow-up about reducing a specific category, setting a spending limit, or drilling into merchants inside the top category.`,
  ),
  spendingTime: prompt(
    'How has my spending been trending?',
    'Ask AI about your spending trend',
    `The user tapped Summarize on their Overview Spending report (recent months). They want the trend story: is spending rising, falling, or spiky?

Summarize total spending across the visible months, call out the highest and lowest months, and name likely drivers (categories/payees) when tools allow. Flag seasonality or one-off spikes. Suggest a follow-up about the biggest month, a category deep-dive, or a savings target.`,
  ),
  incomeTime: prompt(
    'How steady has my income been?',
    'Ask AI about your income trend',
    `The user tapped Summarize on their Overview Income report (recent months). They want to know if income is steady and what's driving it.

Summarize income across the visible months, highlight the strongest/weakest months, and separate regular pay from irregular income when possible. Suggest a follow-up about income volatility, tax set-asides, or comparing income to spending.`,
  ),
  savingsGoals: prompt(
    'How am I doing on my savings goals?',
    'Ask AI how your savings goals are doing',
    `The user tapped Summarize on their Overview Savings Goals. They want progress clarity and what to prioritize.

Summarize active goals with saved-so-far vs target and pace. Call out goals on track vs behind, and the closest to completion. Suggest a follow-up about boosting a specific goal, adjusting a target date, or reallocating contributions.`,
  ),
  watchList: prompt(
    'Anything on my watchlist I should worry about?',
    'Ask AI about your watchlist',
    `The user tapped Summarize on their Overview Watchlist. They want a quick read on tracked payees/categories/tags.

Summarize what is being watched and any notable spend changes vs prior periods. Flag items that spiked or are trending up. Suggest a follow-up about setting a limit, digging into a watched item, or removing noise from the list.`,
  ),
  investmentsGraph: prompt(
    'How are my investments doing?',
    'Ask AI how your investments are doing',
    `The user tapped Summarize on their Overview Investments balances chart. They want performance context, not just the ending balance.

Summarize how investment balances changed over the visible period, biggest contributors to gains/losses, and whether the move looks market-driven vs cash in/out. Suggest a follow-up about allocation, a specific account, or contributions.`,
  ),
  investmentsAcme: prompt(
    "What's moving in my holdings today?",
    'Ask AI what’s moving in your holdings',
    `The user tapped Summarize on their Overview Holdings card. They want today's movers and concentration risk.

Summarize top movers and the largest holdings by weight. Call out big daily swings and any single-position concentration. Suggest a follow-up about a specific holding, diversification, or recent trades.`,
  ),
};

const DEFAULT_PROMPT = prompt(
  "What's the story here?",
  'Ask AI about this Overview section',
  `The user tapped Summarize on an Overview dashboard section. Explain what the numbers mean, highlight the biggest drivers, and end with one useful follow-up question.`,
);

export const getOverviewCardAiPrompt = (cardKey: string): OverviewCardAiPrompt =>
  PROMPTS_BY_CARD_KEY[cardKey] ?? DEFAULT_PROMPT;

export const buildOverviewCardHiddenContextText = (
  cardKey: string,
  title: string,
  snapshot: OverviewCardAiSnapshot,
): string => {
  const cardPrompt = getOverviewCardAiPrompt(cardKey);
  if (snapshot.empty === true) {
    return `${cardPrompt.modelInstructions}\n\nOn-screen snapshot for "${title}": (empty or still loading. Pull fresh data with tools).`;
  }

  const snapshotLines = Object.entries(snapshot)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => {
      if (typeof value === 'object') {
        return `${key}: ${JSON.stringify(value)}`;
      }
      return `${key}: ${String(value)}`;
    });

  const snapshotBlock =
    snapshotLines.length > 0
      ? `On-screen snapshot for "${title}" (hint only; verify with tools):\n${snapshotLines.join('\n')}`
      : `No on-screen snapshot was attached for "${title}". Pull fresh data with tools, and do not assume the view is empty.`;

  return `${cardPrompt.modelInstructions}\n\n${snapshotBlock}`;
};

export type OverviewCardAiSummarizeTrackingProps = {
  source: 'overview-card';
  cardKey: string;
  conversationMode: 'existing';
  useWebSearch: true;
  hasContext: boolean;
  contextLabel: string;
  scopeKey: string;
};

export type OverviewCardAiSummarizeRequest = {
  chat: ChatOpenPayload;
  tracking: OverviewCardAiSummarizeTrackingProps;
};

export const buildOverviewCardAiSummarizeRequest = ({
  cardKey,
  title,
  snapshot,
}: {
  cardKey: string;
  title: string;
  snapshot: OverviewCardAiSnapshot;
}): OverviewCardAiSummarizeRequest => {
  const { userMessage } = getOverviewCardAiPrompt(cardKey);

  return {
    chat: {
      message: userMessage,
      hiddenContext: {
        text: buildOverviewCardHiddenContextText(cardKey, title, snapshot),
        data: {
          source: 'overviewCard',
          cardKey,
          ...snapshot,
        },
      },
      autoSend: true,
      conversationMode: 'existing',
    },
    tracking: {
      source: 'overview-card',
      cardKey,
      conversationMode: 'existing',
      useWebSearch: true,
      hasContext: snapshot.empty !== true,
      contextLabel: title,
      scopeKey: `overview-card:${cardKey}`,
    },
  };
};
